/**
 * Dealer Users & Invites Tab
 * Phase 2E: Master Admin UI
 * 
 * Features:
 * - List of users for this dealer
 * - Invite form (email + role)
 * - Pending invites table with status
 * - Delete users and invitations
 */
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash2, Building2, Send } from "lucide-react";
import UserRooftopAccessModal from "./UserRooftopAccessModal";

const DealerUsersTab = ({ dealer, users, pendingInvites, onRefresh, disabled }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("DEALER_USER");
  const [inviting, setInviting] = useState(false);
  const [deletingInvite, setDeletingInvite] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [rooftopAccessUser, setRooftopAccessUser] = useState(null);
  const dealerInGroup = !!dealer?.dealer_group_id;

  // Render the invite-send result as either a success toast (with Resend
  // message_id suffix) or a loud error toast carrying the Resend error
  // detail. Shared between the initial invite POST and the Resend Invite
  // button so both flows stay consistent.
  const renderInviteResult = (data, fallbackEmail) => {
    if (!data) return;
    if (data.email_sent === false) {
      const errMsg = data.email_error || "Email delivery failed";
      const truncated = errMsg.length > 220 ? errMsg.slice(0, 217) + "…" : errMsg;
      showError(`Invite created but email FAILED: ${truncated}`);
      return;
    }
    if (data.email_provider === "mock") {
      showToast("📧 Mock email sent! Check server logs for invite link.");
      return;
    }
    const target = data.email || fallbackEmail || "user";
    const idSuffix = data.email_message_id
      ? ` (id: ${data.email_message_id.slice(0, 8)}…)`
      : "";
    showToast(`Invitation sent to ${target}${idSuffix}`);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (disabled) {
      showError("Cannot invite users to a disabled dealer");
      return;
    }

    if (!inviteEmail.trim()) {
      showError("Please enter an email address");
      return;
    }

    try {
      setInviting(true);
      const response = await apiFetch(`/admin/dealers/${dealer.id}/invite`, {
        method: "POST",
        data: {
          email: inviteEmail.trim(),
          role: inviteRole,
        },
      });

      // Robust contract: backend returns email_provider, email_sent,
      // email_error, email_message_id. We branch on these flags rather
      // than substring-matching the human message.
      renderInviteResult(response.data, inviteEmail.trim());

      setInviteEmail("");
      setInviteRole("DEALER_USER");
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      setResendingInvite(invite.id);
      const response = await apiFetch(
        `/admin/dealers/${dealer.id}/invites/${invite.id}/resend`,
        { method: "POST" }
      );
      renderInviteResult(response.data, invite.email);
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to resend invitation");
    } finally {
      setResendingInvite(null);
    }
  };

  const handleDeleteInvite = async (inviteId, email) => {
    if (!window.confirm(`Are you sure you want to delete the invitation for ${email}?`)) {
      return;
    }
    
    try {
      setDeletingInvite(inviteId);
      await apiFetch(`/admin/dealers/${dealer.id}/invites/${inviteId}`, {
        method: "DELETE",
      });
      showToast(`Invitation for ${email} has been deleted`);
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to delete invitation");
    } finally {
      setDeletingInvite(null);
    }
  };

  const handleRemoveUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from this dealer? They will lose access to this account.`)) {
      return;
    }
    
    try {
      setDeletingUser(userId);
      await apiFetch(`/admin/dealers/${dealer.id}/users/${userId}`, {
        method: "DELETE",
      });
      showToast(`${email} has been removed from this dealer`);
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to remove user");
    } finally {
      setDeletingUser(null);
    }
  };

  const getInviteStatus = (invite) => {
    if (invite.accepted_at) {
      return { label: "Accepted", variant: "default", className: "bg-green-100 text-green-800" };
    }
    const expiresAt = new Date(invite.expires_at);
    if (expiresAt < new Date()) {
      return { label: "Expired", variant: "secondary", className: "bg-gray-100 text-gray-600" };
    }
    return { label: "Pending", variant: "default", className: "bg-yellow-100 text-yellow-800" };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Invite Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">✉️ Invite New User</CardTitle>
          <CardDescription>
            Send an invitation to add a new user to this dealer.
            {disabled && " (Disabled while dealer is inactive)"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="inviteEmail" className="mb-2 block">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={disabled || inviting}
              />
            </div>
            <div className="w-48">
              <Label className="mb-2 block">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole} disabled={disabled || inviting}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEALER_ADMIN">Dealer Admin</SelectItem>
                  <SelectItem value="DEALER_USER">Dealer User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              type="submit" 
              disabled={disabled || inviting || !inviteEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {inviting ? "Sending..." : "Send Invite"}
            </Button>
          </form>
          {disabled && (
            <p className="text-sm text-red-600 mt-2">
              ⚠️ Invitations cannot be sent while the dealer is disabled.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">👥 Active Users ({users.length})</CardTitle>
          <CardDescription>Users with access to this dealer&apos;s account</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-3xl mb-2">👤</div>
              <p>No users yet. Send an invite to get started.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                          {user.email?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          {(user.first_name || user.last_name) && (
                            <div className="text-xs text-gray-500">
                              {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-xs">
                        {user.role === "DEALER_ADMIN" ? "👑 Admin" : "👤 User"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge 
                        variant={user.is_active ? "default" : "secondary"}
                        className={user.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {(user.role === "DEALER_ADMIN" || user.role === "DEALER_USER") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => dealerInGroup && setRooftopAccessUser(user)}
                            disabled={!dealerInGroup}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 disabled:opacity-40"
                            title={
                              dealerInGroup
                                ? "Manage rooftop access"
                                : "Add this dealer to a Dealer Group to manage multi-rooftop access"
                            }
                            data-testid={`rooftop-access-btn-${user.id}`}
                          >
                            <Building2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUser(user.id, user.email)}
                          disabled={deletingUser === user.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Remove user from dealer"
                        >
                          {deletingUser === user.id ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pending Invites */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📨 Invitations ({pendingInvites.length})</CardTitle>
          <CardDescription>Pending, accepted, and expired invitations</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingInvites.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-3xl mb-2">✉️</div>
              <p>No invitations sent yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pendingInvites.map((invite) => {
                  const status = getInviteStatus(invite);
                  const canDelete = !invite.accepted_at; // Can't delete accepted invites
                  return (
                    <tr key={invite.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">{invite.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {invite.role === "DEALER_ADMIN" ? "👑 Admin" : "👤 User"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatDate(invite.expires_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={status.className}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {!invite.accepted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvite(invite)}
                              disabled={resendingInvite === invite.id || disabled}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Resend invitation email (rotates the token, restores 72h expiry)"
                              data-testid={`resend-invite-btn-${invite.id}`}
                            >
                              {resendingInvite === invite.id ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvite(invite.id, invite.email)}
                              disabled={deletingInvite === invite.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete invitation"
                              data-testid={`delete-invite-btn-${invite.id}`}
                            >
                              {deletingInvite === invite.id ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Multi-Rooftop: per-user rooftop access modal */}
      {rooftopAccessUser && (
        <UserRooftopAccessModal
          open={!!rooftopAccessUser}
          onClose={() => setRooftopAccessUser(null)}
          user={rooftopAccessUser}
          dealerGroupId={dealer?.dealer_group_id}
        />
      )}
    </div>
  );
};

export default DealerUsersTab;
